/**
 * Invite Management Tab
 * Admin interface for sending, viewing, revoking, and resending registration invites.
 */

import { LoadingSpinner } from '@components/ui';
import { type InviteListItem, useAdminInvites } from '@hooks/useAdminInvites';
import { useInviteActions } from '@hooks/useInviteActions';
import { Check, ChevronLeft, ChevronRight, Copy, Mail, RefreshCw, XCircle } from 'lucide-react';
import { type ChangeEvent, type FormEvent, type ReactElement, useCallback, useState } from 'react';

// =============================================================================
// Status Badge
// =============================================================================

function StatusBadge({ status }: { status: InviteListItem['status'] }): ReactElement {
  const styles: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    used: 'bg-green-500/20 text-green-400 border-green-500/30',
    expired: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    revoked: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[status]}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// =============================================================================
// Component
// =============================================================================

export function InviteManagementTab(): ReactElement {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('free');
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');
  const [magicLink, setMagicLink] = useState('');
  const [copied, setCopied] = useState(false);

  const { invites, total, pages, loading, error, refetch } = useAdminInvites({
    page,
    limit: 50,
    status: statusFilter || undefined,
  });

  const { sendInvite, revokeInvite, resendInvite, loading: actionLoading } = useInviteActions();

  const buildMagicLink = useCallback((code: string, email: string) => {
    const base = window.location.origin;
    return `${base}/register?invite=${encodeURIComponent(code)}&email=${encodeURIComponent(email)}`;
  }, []);

  const handleCopyLink = useCallback(async (link: string) => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const handleSend = async (e: FormEvent) => {
    e.preventDefault();
    setSendError('');
    setSendSuccess('');
    setMagicLink('');

    const result = await sendInvite(newEmail, newRole);
    if (result.success) {
      const link = result.invite ? buildMagicLink(result.invite.code, result.invite.email) : '';
      setMagicLink(link);
      setSendSuccess(`Invite created for ${newEmail}`);
      setNewEmail('');
      setNewRole('free');
      refetch();
    } else {
      setSendError(result.error || 'Failed to send invite');
    }
  };

  const handleRevoke = async (invite: InviteListItem) => {
    const result = await revokeInvite(invite.id);
    if (result.success) {
      refetch();
    }
  };

  const handleResend = async (invite: InviteListItem) => {
    const result = await resendInvite(invite.id);
    if (result.success) {
      refetch();
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pages) setPage(newPage);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Send Invite Form */}
      <div className="bg-theme-surface rounded-xl border border-theme-border p-4">
        <h3 className="text-sm font-semibold text-theme-text mb-3">Send Invite</h3>
        <form onSubmit={handleSend} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="inviteEmail" className="block text-xs text-theme-text-secondary mb-1">
              Email
            </label>
            <input
              id="inviteEmail"
              type="email"
              value={newEmail}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setNewEmail(e.target.value)}
              required
              placeholder="user@example.com"
              className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-white placeholder-gray-500 text-sm focus:outline-none focus:border-theme-primary"
            />
          </div>
          <div className="w-32">
            <label htmlFor="inviteRole" className="block text-xs text-theme-text-secondary mb-1">
              Role
            </label>
            <select
              id="inviteRole"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-white text-sm focus:outline-none focus:border-theme-primary"
            >
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="premium">Premium</option>
              <option value="elite">Elite</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={actionLoading || !newEmail}
            className="px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:opacity-50 text-theme-on-primary-fill font-medium rounded-lg text-sm transition-colors flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            Send
          </button>
        </form>
        {sendError && <p className="mt-2 text-sm text-theme-danger">{sendError}</p>}
        {sendSuccess && (
          <div className="mt-3 space-y-2">
            <p className="text-sm text-green-400">{sendSuccess}</p>
            {magicLink && (
              <div className="flex items-center gap-2 p-3 bg-theme-bg rounded-lg border border-theme-border">
                <input
                  type="text"
                  readOnly
                  value={magicLink}
                  className="flex-1 bg-transparent text-white text-xs font-mono truncate outline-none"
                />
                <button
                  type="button"
                  onClick={() => handleCopyLink(magicLink)}
                  className="px-3 py-1.5 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill text-xs font-medium rounded transition-colors flex items-center gap-1.5 shrink-0"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Status Filter */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-theme-text-secondary font-semibold">{total} invites</span>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-1.5 bg-theme-bg border border-theme-border rounded text-white text-sm focus:outline-none focus:border-theme-primary"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="used">Used</option>
          <option value="expired">Expired</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 bg-theme-danger/10 border border-theme-danger rounded-lg text-theme-danger">
          {error}
        </div>
      )}

      {/* Invites Table */}
      {!loading && !error && (
        <>
          <div className="bg-theme-surface rounded-xl border border-theme-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-theme-border">
                    <th className="px-4 py-3 text-left text-theme-text-secondary font-medium">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-theme-text-secondary font-medium">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-theme-text-secondary font-medium">
                      Role
                    </th>
                    <th className="px-4 py-3 text-left text-theme-text-secondary font-medium">
                      Invited By
                    </th>
                    <th className="px-4 py-3 text-left text-theme-text-secondary font-medium">
                      Sent
                    </th>
                    <th className="px-4 py-3 text-left text-theme-text-secondary font-medium">
                      Expires
                    </th>
                    <th className="px-4 py-3 text-right text-theme-text-secondary font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-theme-border">
                  {invites.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-theme-text-secondary">
                        No invites found
                      </td>
                    </tr>
                  ) : (
                    invites.map((invite) => (
                      <tr key={invite.id} className="hover:bg-theme-bg/50 transition-colors">
                        <td className="px-4 py-3 text-white font-mono text-xs">{invite.email}</td>
                        <td className="px-4 py-3">
                          <StatusBadge status={invite.status} />
                        </td>
                        <td className="px-4 py-3 text-theme-text-secondary capitalize">
                          {invite.role}
                        </td>
                        <td className="px-4 py-3 text-theme-text-secondary">Admin</td>
                        <td className="px-4 py-3 text-theme-text-secondary text-xs">
                          {formatDate(invite.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-theme-text-secondary text-xs">
                          {formatDate(invite.expiresAt)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {invite.status === 'pending' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleCopyLink(buildMagicLink(invite.code, invite.email))
                                  }
                                  title="Copy invite link"
                                  className="p-1.5 hover:bg-theme-bg rounded transition-colors text-theme-text-secondary hover:text-theme-primary disabled:opacity-50"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleResend(invite)}
                                  disabled={actionLoading}
                                  title="Resend"
                                  className="p-1.5 hover:bg-theme-bg rounded transition-colors text-theme-text-secondary hover:text-white disabled:opacity-50"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRevoke(invite)}
                                  disabled={actionLoading}
                                  title="Revoke"
                                  className="p-1.5 hover:bg-theme-bg rounded transition-colors text-theme-text-secondary hover:text-theme-danger disabled:opacity-50"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {(invite.status === 'expired' || invite.status === 'revoked') && (
                              <button
                                type="button"
                                onClick={() => handleResend(invite)}
                                disabled={actionLoading}
                                title="Resend"
                                className="p-1.5 hover:bg-theme-bg rounded transition-colors text-theme-text-secondary hover:text-white disabled:opacity-50"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-theme-text-secondary">
                Page {page} of {pages}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 bg-theme-bg border border-theme-border text-theme-text rounded-lg hover:bg-theme-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>
                <button
                  type="button"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pages}
                  className="px-4 py-2 bg-theme-bg border border-theme-border text-theme-text rounded-lg hover:bg-theme-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
